import { supabase } from '../lib/supabase';
import { SUPABASE_NOT_CONFIGURED_MSG } from '../lib/envConfig';

const assertSupabase = () => {
  if (!supabase) throw new Error(SUPABASE_NOT_CONFIGURED_MSG);
};
import { restaurantProfile as defaultRestaurantProfile } from '../data/restaurantData';
import { dishesData } from '../data/dishesData';
import { categoriesData } from '../data/categoriesData';

// ─── Restaurant Profile ───────────────────────────────────────────────────────

export const fetchRestaurantProfile = async () => {
  if (!supabase) return { ...defaultRestaurantProfile };
  const { data, error } = await supabase?.from('restaurant_profile')?.select('*')?.limit(1)?.single();
  if (error) throw error;
  return {
    id: data?.id,
    name: data?.name,
    tagline: data?.tagline,
    cuisine: data?.cuisine,
    phone: data?.phone,
    email: data?.email,
    address: data?.address,
    hours: data?.hours,
    currency: data?.currency,
    logo: data?.logo,
    logoAlt: data?.logo_alt,
  };
};

export const updateRestaurantProfile = async (profile) => {
  assertSupabase();
  const { data: existing } = await supabase?.from('restaurant_profile')?.select('id')?.limit(1)?.single();

  const payload = {
    name: profile?.name,
    tagline: profile?.tagline,
    cuisine: profile?.cuisine,
    phone: profile?.phone,
    email: profile?.email,
    address: profile?.address,
    hours: profile?.hours,
    currency: profile?.currency,
    logo: profile?.logo,
    logo_alt: profile?.logoAlt || profile?.logo_alt || '',
    updated_at: new Date()?.toISOString(),
  };

  if (existing?.id) {
    const { error } = await supabase?.from('restaurant_profile')?.update(payload)?.eq('id', existing?.id);
    if (error) throw error;
  } else {
    const { error } = await supabase?.from('restaurant_profile')?.insert(payload);
    if (error) throw error;
  }
};

// ─── Categories ───────────────────────────────────────────────────────────────

export const fetchCategories = async () => {
  if (!supabase) {
    return categoriesData
      ?.filter(c => c?.id !== 'all')
      ?.map((cat, i) => ({ id: cat?.id, name: cat?.name, sortOrder: i, itemCount: 0 }));
  }
  const { data, error } = await supabase?.from('menu_categories')?.select('*')?.order('sort_order', { ascending: true });
  if (error) throw error;
  return data?.map((cat) => ({
    id: cat?.id,
    name: cat?.name,
    sortOrder: cat?.sort_order,
    itemCount: 0,
  }));
};

export const addCategory = async (name) => {
  assertSupabase();
  const id = name?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '') || `cat-${Date.now()}`;
  const { data: existing } = await supabase?.from('menu_categories')?.select('sort_order')?.order('sort_order', { ascending: false })?.limit(1)?.single();
  const sortOrder = (existing?.sort_order || 0) + 1;
  const { error } = await supabase?.from('menu_categories')?.insert({ id, name, sort_order: sortOrder });
  if (error) throw error;
  return { id, name, sortOrder, itemCount: 0 };
};

export const updateCategory = async (id, name) => {
  assertSupabase();
  const { error } = await supabase?.from('menu_categories')?.update({ name })?.eq('id', id);
  if (error) throw error;
};

export const deleteCategory = async (id) => {
  assertSupabase();
  const { error } = await supabase?.from('menu_categories')?.delete()?.eq('id', id);
  if (error) throw error;
};

// ─── Dishes ───────────────────────────────────────────────────────────────────

export const fetchDishes = async () => {
  if (!supabase) return dishesData?.map(d => ({ ...d }));
  const { data, error } = await supabase?.from('menu_dishes')?.select('*')?.order('sort_order', { ascending: true });
  if (error) throw error;
  return data?.map(dbDishToApp);
};

export const upsertDish = async (dish) => {
  assertSupabase();
  const payload = appDishToDb(dish);
  const { error } = await supabase?.from('menu_dishes')?.upsert(payload, { onConflict: 'id' });
  if (error) throw error;
};

export const upsertDishes = async (dishes) => {
  assertSupabase();
  const payloads = dishes?.map(appDishToDb);
  const { error } = await supabase?.from('menu_dishes')?.upsert(payloads, { onConflict: 'id' });
  if (error) throw error;
};

export const insertDish = async (dish) => {
  if (!supabase) {
    const newId = Date.now();
    return { ...dish, id: newId };
  }
  // Generate a numeric id based on timestamp
  const newId = Date.now();
  const payload = appDishToDb({ ...dish, id: newId });
  const { error } = await supabase?.from('menu_dishes')?.insert(payload);
  if (error) throw error;
  return { ...dish, id: newId };
};

export const deleteDish = async (id) => {
  assertSupabase();
  const { error } = await supabase?.from('menu_dishes')?.delete()?.eq('id', id);
  if (error) throw error;
};

// ─── Real-time subscriptions ──────────────────────────────────────────────────

export const subscribeToMenuDishes = (callback) => {
  if (!supabase) return null;
  return supabase?.channel('menu_dishes_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'menu_dishes' }, callback)?.subscribe();
};

export const subscribeToCategories = (callback) => {
  if (!supabase) return null;
  return supabase?.channel('menu_categories_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'menu_categories' }, callback)?.subscribe();
};

export const subscribeToRestaurantProfile = (callback) => {
  if (!supabase) return null;
  return supabase?.channel('restaurant_profile_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_profile' }, callback)?.subscribe();
};

// ─── Converters ───────────────────────────────────────────────────────────────

function dbDishToApp(d) {
  return {
    id: d?.id,
    name: d?.name,
    description: d?.description || '',
    price: parseFloat(d?.price) || 0,
    originalPrice: d?.original_price ? parseFloat(d?.original_price) : null,
    category: d?.category,
    image: d?.image || '',
    imageAlt: d?.image_alt || '',
    ingredients: d?.ingredients || [],
    calories: d?.calories || 0,
    isNew: d?.is_new || false,
    isPopular: d?.is_popular || false,
    isVegetarian: d?.is_vegetarian || false,
    spiceLevel: d?.spice_level || 0,
    arUrl: d?.ar_url || '',
    hidden: d?.hidden || false,
    sortOrder: d?.sort_order || 0,
    widthCm: d?.width_cm ? parseFloat(d?.width_cm) : null,
    heightCm: d?.height_cm ? parseFloat(d?.height_cm) : null,
    depthCm: d?.depth_cm ? parseFloat(d?.depth_cm) : null,
    chips: Array.isArray(d?.chips) ? d?.chips : (d?.chip ? [d?.chip] : []),
  };
}

function appDishToDb(d) {
  return {
    id: d?.id,
    name: d?.name,
    description: d?.description || '',
    price: parseFloat(d?.price) || 0,
    original_price: d?.originalPrice ? parseFloat(d?.originalPrice) : null,
    category: d?.category || 'mains',
    image: d?.image || '',
    image_alt: d?.imageAlt || d?.image_alt || '',
    ingredients: d?.ingredients || [],
    calories: d?.calories || 0,
    is_new: d?.isNew || d?.is_new || false,
    is_popular: d?.isPopular || d?.is_popular || false,
    is_vegetarian: d?.isVegetarian || d?.is_vegetarian || false,
    spice_level: d?.spiceLevel || d?.spice_level || 0,
    ar_url: d?.arUrl || d?.ar_url || '',
    hidden: d?.hidden || false,
    sort_order: d?.sortOrder || d?.sort_order || 0,
    width_cm: d?.widthCm != null ? parseFloat(d?.widthCm) : (d?.width_cm != null ? parseFloat(d?.width_cm) : null),
    height_cm: d?.heightCm != null ? parseFloat(d?.heightCm) : (d?.height_cm != null ? parseFloat(d?.height_cm) : null),
    depth_cm: d?.depthCm != null ? parseFloat(d?.depthCm) : (d?.depth_cm != null ? parseFloat(d?.depth_cm) : null),
    chips: Array.isArray(d?.chips) ? d?.chips : [],
    updated_at: new Date()?.toISOString(),
  };
}
